import {
  isClassificationColumnMapped,
  isItgcFromClassificationCell,
  isMappedClassificationColumnValid,
  resolveTypesWhenClassificationUntrusted,
} from './detect-category/index.js';

function cellRaw(row, colIndex) {
  if (colIndex === undefined || colIndex === null || colIndex < 0) return '';
  if (!row || colIndex >= row.length) return '';
  return row[colIndex] == null ? '' : String(row[colIndex]);
}

function cellTrim(row, colIndex) {
  return cellRaw(row, colIndex).trim();
}

function splitCodeAndName(value) {
  const v = (value || '').trim();
  if (v === '') return ['', ''];
  const sp = v.indexOf(' ');
  if (sp < 0) return [v, ''];
  return [v.slice(0, sp).trim(), v.slice(sp + 1).trim()];
}

function splitMultiValue(value) {
  const v = (value || '').trim();
  if (v === '') return [];
  return v
    .split(';;')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/** Key Reports cell: comma-separated names (not ";;" like Risks). */
function splitCommaValues(value) {
  const v = (value || '').trim();
  if (v === '') return [];
  return v
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

function parseBoolish(value) {
  const v = (value || '').trim().toLowerCase();
  return v === 'y' || v === 'yes' || v === 'true' || v === '1';
}

/**
 * @param {string[][]} rows
 * @param {number} headerRowIndex
 * @param {Record<string, number>} mapping fieldKey → column index
 */
export function buildGenericRcmFileData(rows, headerRowIndex, mapping) {
  const list = Array.isArray(rows) ? rows : [];
  const map = mapping || {};
  const uidCol = map.client_control_id;
  if (uidCol === undefined || uidCol === null || uidCol === '') {
    const err = new Error('Map Control UID before building preview.');
    err.userFacing = true;
    throw err;
  }

  const headerRow = list[headerRowIndex] || [];
  const classificationMapped = isClassificationColumnMapped(map);

  // Trust check: only use Classification mapping when column looks real.
  let classificationTrusted = false;
  if (classificationMapped) {
    const classCol = Number(map.classification);
    const headerLabel = headerRow[classCol] == null ? '' : String(headerRow[classCol]);
    const samples = [];
    for (let i = headerRowIndex + 1; i < list.length && samples.length < 25; i++) {
      const row = list[i] || [];
      if (cellTrim(row, map.client_control_id) === '') continue;
      samples.push(cellTrim(row, classCol));
    }
    classificationTrusted = isMappedClassificationColumnValid(headerLabel, samples);
  }

  // Collect UID rows first so detect can decide file-level fallback vs detect.
  const uidRows = [];
  for (let i = headerRowIndex + 1; i < list.length; i++) {
    const row = list[i] || [];
    const clientControlId = cellTrim(row, map.client_control_id);
    if (clientControlId === '') continue;
    uidRows.push({
      rowIndex: i,
      row,
      clientControlId,
      processName: cellTrim(row, map.process_name),
      entityName: cellTrim(row, map.entity_name),
      classificationCell: classificationMapped ? cellTrim(row, map.classification) : '',
    });
  }

  let typeMode = 'classification'; // classification | detect | fallback
  let isItgcByUidIndex = null;

  if (classificationTrusted) {
    typeMode = 'classification';
    isItgcByUidIndex = uidRows.map((r) => isItgcFromClassificationCell(r.classificationCell));
  } else {
    const resolved = resolveTypesWhenClassificationUntrusted(
      uidRows.map((r) => ({
        processName: r.processName,
        entityName: r.entityName,
        classificationCell: r.classificationCell,
      }))
    );
    typeMode = resolved.useDetect ? 'detect' : 'fallback';
    isItgcByUidIndex = resolved.isItgcByIndex;
  }

  const out = [];
  const fileWarnings = [];

  if (!classificationMapped) {
    fileWarnings.push(
      typeMode === 'detect'
        ? 'Classification is not mapped. Type is based on System / Subprocess where possible.'
        : 'Classification is not mapped. Type is set to Business.'
    );
  } else if (!classificationTrusted) {
    fileWarnings.push(
      typeMode === 'detect'
        ? 'The mapped Classification column does not look like Classification. Type is based on System / Subprocess where possible.'
        : 'The mapped Classification column does not look like Classification. Type is set to Business.'
    );
  }

  for (let u = 0; u < uidRows.length; u++) {
    const { row, rowIndex, clientControlId, processName, entityName, classificationCell } = uidRows[u];
    const isItgc = Boolean(isItgcByUidIndex[u]);
    const classification = classificationTrusted ? classificationCell : '';
    const cycleRaw = cellTrim(row, map.cycle_or_domain);
    const [cycleCode, cycleName] = splitCodeAndName(cycleRaw);
    const risks = splitMultiValue(cellRaw(row, map.risks));

    const parsed = {
      rowNumber: rowIndex + 1,
      classification,
      entityName,
      cycleOrDomainCode: cycleCode,
      cycleOrDomainName: cycleName,
      processName,
      clientControlId,
      controlTitle: cellTrim(row, map.control_title),
      controlDescription: cellTrim(row, map.control_description),
      controlObjective: cellTrim(row, map.control_objective),
      controlOwner: cellTrim(row, map.control_owner),
      keyReports: splitCommaValues(cellRaw(row, map.key_reports)),
      frequency: cellTrim(row, map.frequency),
      nature: cellTrim(row, map.nature),
      controlType: cellTrim(row, map.control_type),
      riskLevel: cellTrim(row, map.risk_level),
      risks,
      assertions: splitMultiValue(cellRaw(row, map.assertions)),
      significance: cellTrim(row, map.significance),
      mrcControl: parseBoolish(cellRaw(row, map.mrc_control)),
      isItgc,
      typeSource: typeMode,
      warnings: [],
    };

    if (parsed.controlTitle === '') parsed.warnings.push('control title is empty');
    if (isItgc && parsed.entityName === '') parsed.warnings.push('ITGC row missing system / entity name');
    if (!isItgc && parsed.processName === '') {
      parsed.warnings.push('no Subprocess mapped — process may be derived from cycle name');
    }
    if (cycleRaw === '') parsed.warnings.push('missing Cycle or Domain value');
    if (risks.length === 0) {
      parsed.warnings.push('no Risks on this row — import will skip it');
    }
    if (isItgc && risks.length === 0) {
      parsed.warnings.push(
        'ITGC row has no Risks — control will not appear until at least one risk is linked'
      );
    }

    out.push(parsed);
  }

  if (out.length === 0) {
    fileWarnings.push('No data rows with a Control UID were found under the selected header.');
  }

  const itgcRows = out.filter((r) => r.isItgc).length;
  const warningRows = out.filter((r) => r.warnings.length > 0).length;
  const rowsWithRisks = out.filter((r) => (r.risks || []).length > 0).length;

  return {
    totalRows: out.length,
    itgcRows,
    businessRows: out.length - itgcRows,
    warningRows,
    rowsWithRisks,
    typeMode,
    fileWarnings,
    preview: out,
    fileData: out,
  };
}
